import React, { ButtonHTMLAttributes, forwardRef, FunctionComponent } from "react";
import Menu from "@mui/material/Menu";
import { IconButton, ThemeProvider } from "@hxnova/react-components";
import { Icon } from "@hxnova/icons";
import { NovaTheme } from "@hxnova/themes";

import NavBarRedesignOverflowItem from "./nav_bar_redesign_overflow_item";
import PageNames from "../../../models/domain/enums/page_names";
import Tooltipable from "../../shared/tooltipable";
import usePopover from "../../../services/component_helpers/component_effects/use_popover";
import { NavRouteContext } from "./nav_bar_redesign_route";
import { RedesignNavItem } from "./nav_bar_redesign_options";

type OverflowTriggerButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  active: boolean
};

const OverflowTriggerButton = forwardRef<HTMLButtonElement, OverflowTriggerButtonProps>(
  ({ active, className, ...rest }, ref) => (
    <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
      <IconButton
        ref={ref}
        variant="neutral"
        aria-label="More"
        aria-haspopup="menu"
        aria-pressed={active}
        className={`NavBarRedesign-iconButton${className ? ` ${className}` : ""}${active ? " Nova-selected" : ""}`}
        {...rest}
      >
        <Icon name="more_horiz" family="material" size={24}/>
      </IconButton>
    </ThemeProvider>
  )
);
OverflowTriggerButton.displayName = "OverflowTriggerButton";

export type Props = {
  items: RedesignNavItem[]
  pageName: PageNames
  routeContext: NavRouteContext
};

// The "..." overflow, styled to the Figma menu (see nav_bar_redesign.css). Items arrive already
// filtered by their `visible` predicates; routing and the selected row use the same pageNames /
// resolveNavRoute logic as the rest of the reskinned nav. Hidden entirely when no items are visible.
const NavBarRedesignOverflowMenu: FunctionComponent<Props> = ({ items, pageName, routeContext }) => {
  const [open, anchorEl, handleOpen, onClose] = usePopover();

  if (items.length === 0) {
    return null;
  }

  // The trigger is selected while the current page is one of its items (Figma "Selected=More").
  const active = items.some((item) => item.base.pageNames.has(pageName));

  return <>
    <Tooltipable title="More">
      <OverflowTriggerButton
        active={active}
        onClick={handleOpen}
        aria-controls={open ? "NavBarRedesign-overflowMenu" : undefined}
        aria-expanded={open}
      />
    </Tooltipable>
    <Menu
      id="NavBarRedesign-overflowMenu"
      className="NavBarRedesign-overflowMenu"
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      onClose={onClose}
      onClick={onClose}
    >
      {items.map((item) => <NavBarRedesignOverflowItem
        key={item.key}
        item={item}
        selected={item.base.pageNames.has(pageName)}
        routeContext={routeContext}
        onClose={onClose}
      />)}
    </Menu>
  </>;
};

export default NavBarRedesignOverflowMenu;
